/**
 * Deck guardrails — a "Check deck" button in the editor's top bar and, once run,
 * a strip of findings under it: banned "whitelisting" wording, promised results,
 * unfinished template text, statistics without a source, and every figure against
 * the deck's facts (its campaign, or the brand's campaign ledger). Each finding
 * jumps to its page. The server answers `available: false` when the checker is off.
 *
 * One rule in the copy: a deck whose numbers were never verified is never called
 * "clean". `facts.available === false` turns the green tick grey, keeps the toast
 * a warning, and shows a line saying what went unchecked.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Loader2, ShieldCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import { coworkApi, type GuardrailFinding, type GuardrailReport } from '@/cowork/omniad/api/coworkApi';

const TONE: Record<GuardrailFinding['severity'], { dot: string; text: string; word: string }> = {
  error: { dot: 'bg-[#d92d20]', text: 'text-[#b42318]', word: 'Fix' },
  warning: { dot: 'bg-[#f79009]', text: 'text-[#b54708]', word: 'Check' },
  info: { dot: 'bg-[#667085]', text: 'text-[#475467]', word: 'Consider' },
};

/** What the figures were read against, in the words a reader understands. */
export const factsLabel = (source?: 'campaign' | 'brand') => (source === 'brand' ? 'this brand’s campaigns in the datalake' : 'the campaign data');

export function DeckGuardrails({ presentationId, slidesVersion, onGoToPage, onBeforeCheck, buttonClassName }: {
  presentationId: string;
  /** Any change here marks the last report as stale. */
  slidesVersion: unknown;
  onGoToPage: (page: number) => void;
  /** Save pending edits first so the server checks what the user sees. */
  onBeforeCheck?: () => Promise<void>;
  buttonClassName: string;
}) {
  const [report, setReport] = useState<GuardrailReport | null>(null);
  const [running, setRunning] = useState(false);
  const [stale, setStale] = useState(false);
  const [open, setOpen] = useState(true);
  useEffect(() => { if (report) setStale(true); }, [slidesVersion]);

  const run = async () => {
    setRunning(true);
    try {
      await onBeforeCheck?.();
      const r = await coworkApi.presentations.guardrails(presentationId);
      setReport(r); setStale(false); setOpen(true);
      if (!r.available) toast.message('Deck check is not enabled on this server yet.');
      else if (!r.findings.length) {
        // "Clean" must mean the numbers were read too. When they were not, the toast says what is unchecked.
        if (r.facts?.available) toast.success(`All ${r.pages} pages look clean · ${r.facts.verified} of ${r.facts.figures} figures verified against ${factsLabel(r.facts.source)}${r.facts.charts ? ` (chart values included, ${r.facts.charts} chart${r.facts.charts === 1 ? '' : 's'})` : ''}`);
        else if (r.facts) toast.warning(`Wording looks clean on ${r.pages} pages — but no figure was checked against the datalake. ${r.facts.reason || ''}`.trim());
        else toast.warning(`Wording looks clean on ${r.pages} pages — this server does not check figures yet.`);
      }
    } catch (e) { toast.error((e as Error).message); }
    finally { setRunning(false); }
  };

  const factsNote = !report?.facts ? null
    : report.facts.available
      ? `${report.facts.verified} of ${report.facts.figures} figures verified against ${factsLabel(report.facts.source)}${report.facts.charts ? ` (incl. ${report.facts.charts} chart${report.facts.charts === 1 ? '' : 's'})` : ''}${report.facts.unverified ? `, ${report.facts.unverified} unverified` : ''}${report.facts.unjudged ? ` (${report.facts.unjudged} beyond the per-page limit were not judged)` : ''}`
      : `figures not checked${report.facts.reason ? ` — ${report.facts.reason.replace(/\.$/, '')}` : ''}`;
  const factsUnchecked = !!report?.available && !report.facts?.available;

  // Nothing to flag, but the numbers were never read: a quiet line beats a green tick.
  const noticeOnly = report?.available && open && report.findings.length === 0 && factsUnchecked && (
    <div className="border-b border-[#eaecf0] bg-[#f9fafb] px-4 py-2 text-[12px]">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-3.5 w-3.5 text-[#98a2b3]" />
        <span className="font-semibold text-[#475467]">Wording looks clean on {report.pages} pages — no figure was checked against the datalake</span>
        <span className="text-[#98a2b3]">{report.facts?.reason ? `· ${report.facts.reason}` : report.facts ? '· this deck has no campaign or brand data to check against' : '· this server does not check figures yet'}</span>
        <button onClick={() => setOpen(false)} className="ml-auto rounded p-0.5 text-[#98a2b3] hover:bg-[#f2f4f7]" aria-label="Hide deck check"><X className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );

  const strip = report?.available && open && report.findings.length > 0 && (
    <div className="border-b border-[#fde7c7] bg-[#fffaeb] px-4 py-2 text-[12px]">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-3.5 w-3.5 text-[#b54708]" />
        <span className="font-semibold text-[#93370d]">
          {report.findings.length} thing{report.findings.length === 1 ? '' : 's'} to look at on {new Set(report.findings.map((f) => f.page)).size} page{new Set(report.findings.map((f) => f.page)).size === 1 ? '' : 's'}
          {stale ? ' · deck changed since, run the check again' : ''}
        </span>
        <span className={factsUnchecked ? 'font-semibold text-[#b54708]' : 'text-[#98a2b3]'}>· {report.pages} pages checked{report.unchecked ? `, ${report.unchecked} skipped` : ''}{factsNote ? ` · ${factsNote}` : ''}</span>
        <button onClick={() => setOpen(false)} className="ml-auto rounded p-0.5 text-[#98a2b3] hover:bg-[#fef0c7]" aria-label="Hide deck check"><X className="h-3.5 w-3.5" /></button>
      </div>
      <ul className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
        {report.findings.map((f, i) => (
          <li key={i} className="flex min-w-0 items-center gap-1.5">
            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${TONE[f.severity].dot}`} />
            <button onClick={() => onGoToPage(f.page)} className={`shrink-0 font-semibold underline-offset-2 hover:underline ${TONE[f.severity].text}`} title={f.hint}>Page {f.page}: {f.label}</button>
            <span className="truncate text-[#667085]" title={f.excerpt}>{f.excerpt}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  // The strip lives just under the top bar: portal it into the editor's own slot so the toolbar stays one row.
  const slot = typeof document !== 'undefined' ? document.getElementById('deck-guardrails-slot') : null;
  // The green tick means the whole deck passed — wording AND figures. Unverified numbers keep it grey.
  const clean = report?.available && !report.findings.length && !stale && report.facts?.available !== false;
  return (
    <>
      <button onClick={run} disabled={running} className={buttonClassName} title="Check every page for banned wording, promised results, unfinished text, unsourced statistics — and every figure against the datalake">
        {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className={`h-3.5 w-3.5 ${clean ? 'text-[#067647]' : ''}`} />}
        {report?.available && report.findings.length ? ` ${report.findings.length}` : ' Check'}
      </button>
      {slot && (strip || noticeOnly) ? createPortal(strip || noticeOnly, slot) : null}
    </>
  );
}
